import { useEffect } from 'react';
import { useShadowRootElements } from '@backstage/plugin-techdocs-react';

// This AddOn performs the very specific function of removing a hardcoded local navigation / ToC from documentation pages.
// Specifically, source markdown files for the Private Cloud platform include an "On This Page" section that provides anchor links
// to section within each page. This is redundant since the dynamic right-hand sidebar ToC provides this already.
// At some point, we can remove the "on thi page" content from the source documents and this AddOn no longer be needed, but for now, this
// approach saves us from maintaining different versions of the content.
export const TocFixer = () => {
	/**
	 * The content we are twiddling looks like:
	 * <h2 id="on-this-page">On this page</h2>
	 *
	 * <ul>
	 *     <li>blah</li>
	 *     <li>blah</li>
	 *     <li>blah</li>
	 * </ul>
	 */
	const onThisPageHeading = useShadowRootElements<HTMLImageElement>(['h2#on-this-page']);
	const onThisPageList = useShadowRootElements<HTMLImageElement>(['h2#on-this-page + ul']);
	// to test in browser, something like this can be used: document.querySelector("[data-testid='techdocs-native-shadowroot']").shadowRoot.querySelectorAll('div[data-md-type="toc"] a[href*="on-this-page"]')
	const onThisPageHeadingInToc = useShadowRootElements<HTMLImageElement>(['div[data-md-type="toc"] a[href*="on-this-page"]'] );
	const tocHeading = useShadowRootElements<HTMLLabelElement>(['label[for="__toc"]']);
	const sidebarScrollWrap = useShadowRootElements<HTMLDivElement>(['div[class=md-sidebar__scrollwrap]']);
	const iframeReferrer = useShadowRootElements<HTMLIFrameElement>(['iframe[src*="youtube.com"]']);

	useEffect(() => {
		onThisPageHeading.forEach(match => {
			match.hidden = true;
		});
	}, [onThisPageHeading]);

	useEffect(() => {
		onThisPageList.forEach(match => {
			while (match.firstChild) {
				match.removeChild(match.firstChild);
			}
		});
	}, [onThisPageList]);

	useEffect(() => {
		onThisPageHeadingInToc.forEach(match => {
			const tocList = match.parentNode;
			tocList?.removeChild(match);
		});
	}, [onThisPageHeadingInToc]);

	useEffect(() => {
		tocHeading.forEach(match => {
			match.textContent = 'On this page';
		});
	}, [tocHeading]);

	useEffect(() => {
		sidebarScrollWrap.forEach(match => {
			match.style.overflowY = 'hidden';
		});
	}, [sidebarScrollWrap]);

	// YouTube is requiring Referrer header for embeds, but techdocs DOMPurify strips out referrerpolicy from iframes.
	// There's an open Backstage PR to add allowedAttributes to TechDocs sanitizer config, which would better address this issue.
	// https://github.com/backstage/backstage/pull/27793
	useEffect(() => {
		iframeReferrer.forEach(match => {
			if (match.referrerPolicy === 'strict-origin-when-cross-origin') {
				return;
			}

			const newIframe = match.cloneNode(true) as HTMLIFrameElement;
			newIframe.referrerPolicy = 'strict-origin-when-cross-origin';
			match.parentNode?.replaceChild(newIframe, match);
		});
	}, [iframeReferrer]);

	// Nothing to render directly, so we can just return null.
	return null;
};
